import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';
import { projectKeys } from './projects';
import { workspaceKeys, type StatusCategory } from './workspaces';

export interface WorkspaceTaskFilters {
  assignee_id?: string;
  project_id?: string;
  /** ISO date or ISO 8601 (inclusive lower bound). */
  due_from?: string;
  /** ISO date or ISO 8601 (exclusive upper bound). */
  due_to?: string;
  /** Comma-separated category list, e.g. `"todo,in_progress"`. */
  status_category?: string;
}

export const taskKeys = {
  byProject: (workspaceSlug: string, projectSlug: string) =>
    ['tasks', 'project', workspaceSlug, projectSlug] as const,
  detail: (workspaceSlug: string, taskUlid: string) =>
    ['tasks', 'detail', workspaceSlug, taskUlid] as const,
  byWorkspace: (workspaceSlug: string, args: WorkspaceTaskFilters = {}) =>
    [
      'tasks',
      'workspace',
      workspaceSlug,
      args.assignee_id ?? '',
      args.project_id ?? '',
      args.due_from ?? '',
      args.due_to ?? '',
      args.status_category ?? '',
    ] as const,
};

/**
 * Workspace-level task list. Hard-capped server-side at 500 rows. Use
 * narrow filters (assignee + due range) for the workspace home dashboard
 * to keep the response cheap.
 *
 * `queryOptions` factory shared with route loaders so the same key + fetcher
 * back both `useWorkspaceTasks()` (in components) and `ensureQueryData(...)`
 * preloads.
 */
export const workspaceTasksQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  args: WorkspaceTaskFilters = {},
) =>
  queryOptions({
    queryKey: taskKeys.byWorkspace(workspaceSlug, args),
    queryFn: async () => {
      const query: Record<string, string> = {};
      for (const [k, v] of Object.entries(args)) {
        if (v !== undefined && v !== '') query[k] = v as string;
      }
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/tasks', {
        params: {
          path: { workspace_slug: workspaceSlug },
          query: query as never,
        },
      });
      if (error) throw error;
      return data;
    },
  });

export const useWorkspaceTasks = (
  workspaceSlug: string | undefined,
  args: WorkspaceTaskFilters = {},
) => {
  const api = useApi();
  return useQuery({
    ...workspaceTasksQueryOptions(api, workspaceSlug ?? '', args),
    enabled: Boolean(workspaceSlug),
  });
};

export const tasksQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  projectSlug: string,
) =>
  queryOptions({
    queryKey: taskKeys.byProject(workspaceSlug, projectSlug),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/tasks',
        {
          params: {
            path: { workspace_slug: workspaceSlug, projectSlug },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useTasks = (
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...tasksQueryOptions(api, workspaceSlug ?? '', projectSlug ?? ''),
    enabled: Boolean(workspaceSlug && projectSlug),
  });
};

export const taskQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  taskUlid: string,
) =>
  queryOptions({
    queryKey: taskKeys.detail(workspaceSlug, taskUlid),
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/tasks/{taskUlid}', {
        params: {
          path: { workspace_slug: workspaceSlug, taskUlid },
        },
      });
      if (error) throw error;
      return data;
    },
  });

export const useTask = (
  workspaceSlug: string | undefined,
  taskUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...taskQueryOptions(api, workspaceSlug ?? '', taskUlid ?? ''),
    enabled: Boolean(workspaceSlug && taskUlid),
  });
};

/**
 * Task type — one of `task` (default) | `feature` | `bug` | `chore`.
 * Drives branch-prefix selection on slice 11 GitHub branch creation
 * and renders as a small badge on engineering projects. The picker
 * is hidden on `generic`-type projects (visibility decided by the
 * SPA; the API stores the value either way).
 */
export type TaskType = 'task' | 'feature' | 'bug' | 'chore';

export const TASK_TYPES: ReadonlyArray<TaskType> = ['task', 'feature', 'bug', 'chore'];

export interface CreateTaskInput {
  title: string;
  description?: string;
  type?: TaskType;
  status_id?: string;
  assignee_id?: string;
  due_at?: string;
  parent_task_id?: string;
  tag_ids?: string[];
  /** Glass redesign — Phase 1 backend addition. */
  priority?: 'none' | 'low' | 'medium' | 'high' | 'urgent';
  /** Glass redesign — Phase 1 backend addition. Minutes; capped server-side at 525_600. */
  estimate_minutes?: number;
}

export const useCreateTask = (workspaceSlug: string, projectSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/tasks',
        {
          params: { path: { workspace_slug: workspaceSlug, projectSlug } },
          body: input,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: taskKeys.byProject(workspaceSlug, projectSlug) });
      await qc.invalidateQueries({ queryKey: projectKeys.detail(workspaceSlug, projectSlug) });
    },
  });
};

export interface UpdateTaskInput {
  title?: string;
  description?: string | null;
  type?: TaskType;
  status_id?: string;
  assignee_id?: string | null;
  due_at?: string | null;
  /**
   * `null` promotes the task to top-level. Omit to leave the parent unchanged.
   */
  parent_task_id?: string | null;
  tag_ids?: string[];
  /** Glass redesign — Phase 1 backend addition. */
  priority?: 'none' | 'low' | 'medium' | 'high' | 'urgent';
  /** Glass redesign — Phase 1 backend addition. `null` clears the estimate. */
  estimate_minutes?: number | null;
}

interface TaskListAttrs {
  status_id?: string;
  assignee_id?: string | null;
  due_at?: string | null;
  title?: string;
  description?: string | null;
  type?: TaskType;
  sequence_number?: number;
  /** Server-computed `{prefix}-{sequence}` (slice 10). */
  code?: string;
  parent_task_id?: string | null;
  position?: number;
  tag_ids?: string[];
  completed_at?: string | null;
  [key: string]: unknown;
}

interface TaskListItem {
  id: string;
  attributes?: TaskListAttrs;
}

interface TaskListPayload {
  data?: TaskListItem[];
  [key: string]: unknown;
}

interface TaskDetailPayload {
  data?: { id: string; attributes?: TaskListAttrs; [key: string]: unknown };
  [key: string]: unknown;
}

/**
 * Patch the in-cache attributes of a single task in any cache shape we know
 * about (the project task list + the per-task detail). Returns `undefined`
 * when the target isn't present so callers can no-op.
 */
const patchTaskInList = (
  list: TaskListPayload | undefined,
  taskUlid: string,
  patch: Partial<TaskListAttrs>,
): TaskListPayload | undefined => {
  if (!list?.data) return list;
  const next = list.data.map((row) => {
    if (row.id !== taskUlid) return row;
    return { ...row, attributes: { ...(row.attributes ?? {}), ...patch } };
  });
  return { ...list, data: next };
};

const patchTaskDetail = (
  detail: TaskDetailPayload | undefined,
  patch: Partial<TaskListAttrs>,
): TaskDetailPayload | undefined => {
  if (!detail?.data) return detail;
  return {
    ...detail,
    data: {
      ...detail.data,
      attributes: { ...(detail.data.attributes ?? {}), ...patch },
    },
  };
};

interface CachedStatusItem {
  id: string;
  attributes?: { category?: StatusCategory };
}

interface CachedStatusList {
  data?: CachedStatusItem[];
}

/**
 * Look up a status's category from the workspace-statuses cache. Used by the
 * task-mutation optimistic paths to mirror the server's `completed_at`
 * derivation: the server stamps `completed_at = now()` whenever a task moves
 * INTO a complete-category status, and clears it on any move OUT. Without
 * this client-side mirror, the optimistic patch updates `status_id` but
 * leaves a stale `completed_at`, which is fine for today's UI (the
 * strikethrough is driven by status category lookup, not `completed_at`)
 * but would silently break any future surface that reads `completed_at`
 * directly (a "Completed at <date>" timestamp, a sort key, etc.).
 *
 * Returns:
 * - `string` ISO timestamp if moving INTO a complete-category status
 * - `null` if moving INTO a todo / in-progress category status
 * - `undefined` if the new category can't be resolved (statuses not in
 *   cache, status not found) — caller should leave `completed_at`
 *   untouched in that case rather than guess
 */
const deriveCompletedAtFromStatus = (
  qc: QueryClient,
  workspaceSlug: string,
  newStatusId: string,
): string | null | undefined => {
  const list = qc.getQueryData<CachedStatusList>(workspaceKeys.statuses(workspaceSlug));
  const status = list?.data?.find((s) => s.id === newStatusId);
  const category = status?.attributes?.category;
  if (!category) return undefined;
  return category === 'complete' ? new Date().toISOString() : null;
};

export const useUpdateTask = (workspaceSlug: string, taskUlid: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateTaskInput) => {
      // The spec generated from form-request rules doesn't model nullable strings,
      // but the controller accepts `null` to clear the field. Cast to bypass the
      // type narrowing; runtime contract is correct (covered by Pest tests).
      const { data, error } = await api.PATCH('/v1/workspaces/{workspace_slug}/tasks/{taskUlid}', {
        params: { path: { workspace_slug: workspaceSlug, taskUlid } },
        body: input as {
          title?: string;
          description?: string;
          type?: TaskType;
          status_id?: string;
          assignee_id?: string;
          due_at?: string;
          parent_task_id?: string;
          tag_ids?: string[];
        },
      });
      if (error) throw error;
      return data;
    },
    /**
     * Optimistic patch — instantly updates the project list cache and the
     * per-task detail cache so inline edits feel snappy. When `status_id`
     * changes we also mirror the server's `completed_at` derivation:
     * complete-category status sets a client timestamp, anything else
     * clears the field. Server-issued value lands on settle and overwrites.
     */
    onMutate: async (input) => {
      const detailKey = taskKeys.detail(workspaceSlug, taskUlid);
      const listKeyPrefix: readonly unknown[] = ['tasks', 'project', workspaceSlug];

      await Promise.all([
        qc.cancelQueries({ queryKey: detailKey }),
        qc.cancelQueries({ queryKey: listKeyPrefix }),
      ]);

      const previousDetail = qc.getQueryData<TaskDetailPayload>(detailKey);
      const previousLists: Array<[readonly unknown[], TaskListPayload | undefined]> = qc
        .getQueriesData<TaskListPayload>({ queryKey: listKeyPrefix })
        .map(([key, value]) => [key, value] as const);

      const patch: Partial<TaskListAttrs> = { ...input };

      // Mirror the server's `completed_at` rule when the status changes.
      // `undefined` from the helper means "couldn't resolve the new
      // category" (e.g. statuses not yet in cache) — leave the field
      // alone in that case rather than guess.
      if (input.status_id) {
        const completedAt = deriveCompletedAtFromStatus(qc, workspaceSlug, input.status_id);
        if (completedAt !== undefined) {
          patch.completed_at = completedAt;
        }
      }

      qc.setQueryData<TaskDetailPayload>(detailKey, (old) => patchTaskDetail(old, patch));
      for (const [key] of previousLists) {
        qc.setQueryData<TaskListPayload>(key, (old) => patchTaskInList(old, taskUlid, patch));
      }

      return { previousDetail, previousLists };
    },
    onError: (_err, _input, ctx) => {
      if (!ctx) return;
      qc.setQueryData(taskKeys.detail(workspaceSlug, taskUlid), ctx.previousDetail);
      for (const [key, value] of ctx.previousLists) {
        qc.setQueryData(key, value);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.detail(workspaceSlug, taskUlid) });
      void qc.invalidateQueries({ queryKey: ['tasks', 'project', workspaceSlug] });
    },
  });
};

export interface ReorderTaskInput {
  /**
   * ULID of the task being moved. Carried in the mutate input (not the
   * hook factory) so the mutation closure doesn't depend on a render
   * cycle — important for the board, where the active task changes per
   * drag and React doesn't guarantee a render commit between dnd-kit's
   * `onDragStart` and `onDragEnd`.
   */
  taskUlid: string;
  /** 1-indexed target position within siblings (project_id + parent_task_id). */
  position: number;
  /** Optional new status_id — used for board drag-between-swimlanes. */
  status_id?: string;
}

/**
 * Reorder a task within its sibling group, optionally changing its status
 * in the same operation. The cache mutation mirrors the server's
 * `renumberSiblings` algorithm so positions stay 1..N contiguous and the
 * board doesn't flicker on settle.
 */
export const useReorderTask = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReorderTaskInput) => {
      const { taskUlid, ...body } = input;
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/reorder',
        {
          params: { path: { workspace_slug: workspaceSlug, taskUlid } },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async (input) => {
      const listKeyPrefix: readonly unknown[] = ['tasks', 'project', workspaceSlug];
      await qc.cancelQueries({ queryKey: listKeyPrefix });

      const previousLists: Array<[readonly unknown[], TaskListPayload | undefined]> = qc
        .getQueriesData<TaskListPayload>({ queryKey: listKeyPrefix })
        .map(([key, value]) => [key, value] as const);

      // Mirror the server's `completed_at` derivation when crossing
      // swimlanes — a board drag from "in progress" → "complete" needs
      // the moved card to flip its `completed_at` in the same tick as
      // its `status_id`, otherwise sort-by-completion or completed-at
      // displays would lag a server round-trip behind the visible move.
      const completedAt = input.status_id
        ? deriveCompletedAtFromStatus(qc, workspaceSlug, input.status_id)
        : undefined;

      for (const [key] of previousLists) {
        qc.setQueryData<TaskListPayload>(key, (old) =>
          applyReorderToList(old, input.taskUlid, input.position, input.status_id, completedAt),
        );
      }
      // Patch detail too in case it's mounted alongside the board.
      qc.setQueryData<TaskDetailPayload>(
        taskKeys.detail(workspaceSlug, input.taskUlid),
        (old) =>
          patchTaskDetail(old, {
            position: input.position,
            ...(input.status_id ? { status_id: input.status_id } : {}),
            ...(completedAt !== undefined ? { completed_at: completedAt } : {}),
          }),
      );

      return { previousLists, taskUlid: input.taskUlid };
    },
    onError: (_err, _input, ctx) => {
      if (!ctx) return;
      for (const [key, value] of ctx.previousLists) {
        qc.setQueryData(key, value);
      }
    },
    onSettled: (_data, _err, _input, ctx) => {
      void qc.invalidateQueries({ queryKey: ['tasks', 'project', workspaceSlug] });
      if (ctx?.taskUlid) {
        void qc.invalidateQueries({
          queryKey: taskKeys.detail(workspaceSlug, ctx.taskUlid),
        });
      }
    },
  });
};

export interface RescheduleTaskInput {
  /**
   * ULID of the task being rescheduled. Lives in the mutate input (not
   * the hook factory) for the same reason as `useReorderTask` — calendar
   * drag fires once per gesture and we don't want a per-task hook
   * instance for every chip on the grid.
   */
  taskUlid: string;
  /**
   * New due date as ISO 8601 (`YYYY-MM-DD` or full `YYYY-MM-DDTHH:mm:ss…`).
   * Pass `null` to clear the due date — the controller treats null as
   * "remove from calendar" (the calendar UI never sends null today, but
   * the field is nullable end-to-end).
   */
  due_at: string | null;
}

/**
 * Calendar drag-to-reschedule. Patches a single task's `due_at` and
 * reuses the same project-list / detail / workspace-list optimistic
 * patching as `useUpdateTask`, so dragging a chip across day cells
 * snaps the calendar to its new bucket without waiting for the server.
 *
 * Returned cache key invalidation set is wider than `useUpdateTask` by
 * design — the workspace home calendar widget also reads from
 * `taskKeys.byWorkspace` and needs to refresh when a per-project drag
 * lands.
 */
export const useRescheduleTask = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RescheduleTaskInput) => {
      const { taskUlid, due_at } = input;
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}',
        {
          params: { path: { workspace_slug: workspaceSlug, taskUlid } },
          // Same nullable-cast pattern used in useUpdateTask.
          body: { due_at } as { due_at?: string },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async (input) => {
      const detailKey = taskKeys.detail(workspaceSlug, input.taskUlid);
      const projectListPrefix: readonly unknown[] = ['tasks', 'project', workspaceSlug];
      const workspaceListPrefix: readonly unknown[] = ['tasks', 'workspace', workspaceSlug];

      await Promise.all([
        qc.cancelQueries({ queryKey: detailKey }),
        qc.cancelQueries({ queryKey: projectListPrefix }),
        qc.cancelQueries({ queryKey: workspaceListPrefix }),
      ]);

      const previousDetail = qc.getQueryData<TaskDetailPayload>(detailKey);
      const previousProjectLists: Array<[readonly unknown[], TaskListPayload | undefined]> = qc
        .getQueriesData<TaskListPayload>({ queryKey: projectListPrefix })
        .map(([key, value]) => [key, value] as const);
      const previousWorkspaceLists: Array<[readonly unknown[], TaskListPayload | undefined]> = qc
        .getQueriesData<TaskListPayload>({ queryKey: workspaceListPrefix })
        .map(([key, value]) => [key, value] as const);

      const patch: Partial<TaskListAttrs> = { due_at: input.due_at };

      qc.setQueryData<TaskDetailPayload>(detailKey, (old) => patchTaskDetail(old, patch));
      for (const [key] of previousProjectLists) {
        qc.setQueryData<TaskListPayload>(key, (old) => patchTaskInList(old, input.taskUlid, patch));
      }
      for (const [key] of previousWorkspaceLists) {
        qc.setQueryData<TaskListPayload>(key, (old) => patchTaskInList(old, input.taskUlid, patch));
      }

      return { previousDetail, previousProjectLists, previousWorkspaceLists, taskUlid: input.taskUlid };
    },
    onError: (_err, _input, ctx) => {
      if (!ctx) return;
      qc.setQueryData(taskKeys.detail(workspaceSlug, ctx.taskUlid), ctx.previousDetail);
      for (const [key, value] of ctx.previousProjectLists) {
        qc.setQueryData(key, value);
      }
      for (const [key, value] of ctx.previousWorkspaceLists) {
        qc.setQueryData(key, value);
      }
    },
    onSettled: (_data, _err, _input, ctx) => {
      void qc.invalidateQueries({ queryKey: ['tasks', 'project', workspaceSlug] });
      void qc.invalidateQueries({ queryKey: ['tasks', 'workspace', workspaceSlug] });
      if (ctx?.taskUlid) {
        void qc.invalidateQueries({
          queryKey: taskKeys.detail(workspaceSlug, ctx.taskUlid),
        });
      }
    },
  });
};

/**
 * Mirrors the server controller's `renumberSiblings` logic for optimistic
 * updates. Operates on the project task list cache shape. Pure function —
 * returns a new payload, never mutates input.
 */
const applyReorderToList = (
  list: TaskListPayload | undefined,
  taskUlid: string,
  rawTargetPosition: number,
  newStatusId?: string,
  /**
   * Optimistic `completed_at` value to apply alongside the status
   * change. `undefined` means "leave it alone" (caller couldn't
   * resolve the new category from cache); explicit `null` clears
   * it; an ISO string sets it. Caller derives this via
   * `deriveCompletedAtFromStatus`.
   */
  newCompletedAt?: string | null,
): TaskListPayload | undefined => {
  if (!list?.data) return list;

  const moved = list.data.find((t) => t.id === taskUlid);
  if (!moved) return list;

  const movedAttrs = { ...(moved.attributes ?? {}) };
  if (newStatusId) movedAttrs.status_id = newStatusId;
  if (newCompletedAt !== undefined) movedAttrs.completed_at = newCompletedAt;

  const parentId = movedAttrs.parent_task_id ?? null;
  const siblings = list.data.filter(
    (t) => t.id !== taskUlid && (t.attributes?.parent_task_id ?? null) === parentId,
  );
  // Keep stable sibling ordering by position then id.
  siblings.sort((a, b) => {
    const pa = a.attributes?.position ?? 0;
    const pb = b.attributes?.position ?? 0;
    if (pa !== pb) return pa - pb;
    return a.id.localeCompare(b.id);
  });

  const clamped = Math.max(1, Math.min(rawTargetPosition, siblings.length + 1));

  // Rebuild the sibling order with `moved` slotted in at `clamped`.
  const ordered: TaskListItem[] = [];
  let cursor = 1;
  let inserted = false;
  for (const s of siblings) {
    if (!inserted && cursor === clamped) {
      ordered.push({ ...moved, attributes: { ...movedAttrs, position: cursor } });
      inserted = true;
      cursor++;
    }
    ordered.push({ ...s, attributes: { ...(s.attributes ?? {}), position: cursor } });
    cursor++;
  }
  if (!inserted) {
    ordered.push({ ...moved, attributes: { ...movedAttrs, position: cursor } });
  }

  // Index renumbered tasks by id so we can splice them back into the full list.
  const renumberedById = new Map(ordered.map((t) => [t.id, t]));
  const next = list.data.map((row) => renumberedById.get(row.id) ?? row);
  return { ...list, data: next };
};
